import { action, makeObservable, observable, runInAction } from 'mobx';

import { requireService } from 'src/packages/di';

import type { UserExportTaskResponse } from './UserFileGenerationService';
import { UserFileGenerationApi } from './UserFileGenerationService';

export class GeneratedUserFilesService {
  api = new UserFileGenerationApi();

  @observable
  tasks: UserExportTaskResponse[] = [];

  @observable
  isLoading = false;

  @observable
  hasUpdates = false;

  @observable
  sortBy: 'createdAt' = 'createdAt';

  @observable
  sortOrder: 'asc' | 'desc' = 'asc';

  constructor(private readonly stompOperationsPublisherService = requireService('stompOperationsPublisherService')) {
    makeObservable(this);
    this.stompOperationsPublisherService.stompPublisher.subscribe('/export-tasks/notifications', (message) => {
      const task = message as unknown as UserExportTaskResponse;
      if (task.taskId) {
        this.addTask(task.taskId);
        this.setHasUpdates(true);
      }
    });
  }

  get sortedTasks() {
    return this.tasks.slice().sort((a, b) => {
      return this.sortOrder === 'asc'
        ? new Date(a.createdAt).valueOf() - new Date(b.createdAt).valueOf()
        : new Date(b.createdAt).valueOf() - new Date(a.createdAt).valueOf();
    });
  }

  @action.bound
  updateTask(task: UserExportTaskResponse) {
    const index = this.tasks.findIndex((file) => file.taskId === task.taskId);
    if (index !== -1) {
      this.tasks[index] = task;
    } else {
      this.tasks.push(task);
    }
  }

  @action.bound
  setHasUpdates(hasUpdates: boolean) {
    this.hasUpdates = hasUpdates;
  }

  @action.bound
  setIsLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  getUserTasks() {
    this.setIsLoading(true);
    this.api
      .fetchTasks()
      .then((tasks) => {
        runInAction(() => {
          this.tasks = tasks;
        });
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.setIsLoading(false);
      });
  }

  addTask(taskId: string) {
    this.api
      .fetchTask(taskId)
      .then((task) => {
        this.updateTask(task);
      })
      .catch((e) => {
        console.error(e);
      });
  }

  setSortOrder(sortOrder: 'asc' | 'desc') {
    this.sortOrder = sortOrder;
  }

  toggleSortOrder() {
    this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
  }
}
